/**
 * A contract term's true length in years, read from the dates the retailer quoted.
 *
 * Lives here rather than beside its caller so the engine, the proposal renderer and the Offer
 * Details table all read one definition. When they diverged, the screen showed a term one
 * multiplier and the generated tender another, for the same offer.
 *
 * @see termLengthInYearsFromDates for the two guarantees this file exists to hold.
 */

/**
 * How far a term's parsed end date may sit from the end its own month count implies before the
 * dates are rejected. Fifteen days is half a month: wide enough for AGL's 12-day tail and for
 * month-end rounding, narrow enough that a term out by a whole month or more keeps its label.
 */
export const MAX_TERM_LABEL_DRIFT_DAYS = 15;

/**
 * A parsed length must land within this band of the term's nominal months to be believed.
 *
 * ⚠️ The parsed date ranges cannot be trusted on their own. Measured across prod's 8,522 dated
 * terms, 292 of them (184 offers, 107 tenders) produce a length between 0.14x and 601x their
 * stated month count: a 40-month term whose ranges span 37 years, a 12-month one spanning
 * three. Feeding those straight into the term multiplier would mis-price whole tenders by
 * orders of magnitude — far worse than the odd-days tail this correction exists to fix.
 */
const MIN_PLAUSIBLE_RATIO = 0.9;
const MAX_PLAUSIBLE_RATIO = 1.15;

const MS_PER_DAY = 86_400_000;

/** 29 Februaries falling in (start, end], both epoch-ms UTC midnights. */
export function countLeapDays(start: number, end: number): number {
    const firstYear = new Date(start).getUTCFullYear();
    const lastYear = new Date(end).getUTCFullYear();
    let count = 0;
    for (let year = firstYear; year <= lastYear; year++) {
        if (year % 4 !== 0 || (year % 100 === 0 && year % 400 !== 0)) continue;
        const feb29 = Date.UTC(year, 1, 29);
        if (feb29 > start && feb29 <= end) count++;
    }
    return count;
}

const parseDay = (value: unknown): number | null => {
    if (typeof value !== 'string') return null;
    const ms = Date.parse(`${value.slice(0, 10)}T00:00:00Z`);
    return isNaN(ms) ? null : ms;
};

const rangeOf = (obj: any): { start: number | null; end: number | null } => {
    const range = obj?.date_range ?? obj?.dateRange;
    return {
        start: parseDay(range?.start_date ?? range?.startDate),
        end: parseDay(range?.end_date ?? range?.endDate),
    };
};

/**
 * A contract term's true length in years, read from its own date range. Returns null — leaving
 * the caller on the termMonths / 12 form — when the term carries no usable dates, when its month
 * count is a clean multiple of 12, or when its dates contradict its own label.
 *
 * Retailers do not quote whole months. AGL's terms are "4 Months 12 Days", "16 Months 12 Days",
 * and so on: its 16-month term runs 20/08/2026 – 31/12/2027, which is 498 days = 1.364384 years
 * against the 1.330594 that termMonths / 12 − 1/365 gives. That 2.47% shortfall printed AGL's
 * commodity spend at $18,836 against the client model's $19,311, and its Other Charges at
 * $6,358 against $6,519 — on every one of its terms, since all four carry the same 12-day tail.
 *
 * TWO GUARANTEES, both load-bearing:
 *
 *   1. Whole-year terms never reach the dates. A term labelled 12 / 24 / 36 / 48 months is a
 *      round-years quote and the anniversary convention is already right for it. Prod holds 362
 *      whole-year terms whose parsed end date disagrees with their own month count by up to 92
 *      days — month-end rounding, a rate period captured instead of the term, a stepped offer's
 *      first block. Letting dates win there would move 362 standard terms to chase 219 that are
 *      probably mislabelled, so the month count wins outright whenever it is a clean multiple
 *      of 12 and every standard term keeps the figure it prints today, bit for bit.
 *
 *   2. Leap days are excluded, which is what makes this exact rather than merely close. A
 *      standard N-year term runs 365N + leapDays − 1 days, so removing the leap days leaves
 *      365N − 1 and the result is N − 1/365, identical to termWeightFor() for every start date.
 *      Counting them would hand every leap-spanning 24-month term 2.000000 against today's
 *      1.997260 — a +0.14% drift across thousands of offers that nobody asked for. It also
 *      reproduces the client workbook exactly: WPG SYDNEY's four terms come to
 *      1.36438356164384 / 2.36438356164384 / 3.36438356164384 / 4.36438356164384, matching its
 *      Data Input Page to fourteen decimal places.
 */
export function termLengthInYearsFromDates(termData: any, termMonths: number): number | null {
    if (!(termMonths > 0)) return null;

    // Guarantee 1 — a round-years label is the more reliable signal; the dates are not consulted.
    if (termMonths % 12 === 0) return null;

    // Prefer a range on the term itself, then fall back to the span of its year slots. Both
    // shapes occur in prod: some offers store the term's own date_range, others leave it null
    // and carry the dates on year_N entries only. Reading just the term-level range made this
    // whole correction a silent no-op on every offer of the second shape.
    let { start, end } = rangeOf(termData);
    if (start === null || end === null) {
        for (const key of Object.keys(termData || {})) {
            if (!/^year_\d+$/.test(key)) continue;
            const yearRange = rangeOf((termData as any)[key]);
            if (yearRange.start !== null) start = start === null ? yearRange.start : Math.min(start, yearRange.start);
            if (yearRange.end !== null) end = end === null ? yearRange.end : Math.max(end, yearRange.end);
        }
    }
    if (start === null || end === null) return null;

    const rawDays = Math.round((end - start) / MS_PER_DAY);
    if (!Number.isFinite(rawDays) || rawDays <= 0) return null;

    // Guarantee 2 — leap days excluded.
    const years = (rawDays - countLeapDays(start, end)) / 365;

    // The dates must corroborate the term's own label before they are allowed to override it.
    // AGL's 12-day tail is a genuine remainder the month count lost to rounding, and exactly
    // what this is for. A term whose dates land a month or more away from its label is a
    // different animal: one of the two is wrong and there is no way to tell which.
    const impliedEnd = new Date(start);
    impliedEnd.setUTCMonth(impliedEnd.getUTCMonth() + termMonths);
    impliedEnd.setUTCDate(impliedEnd.getUTCDate() - 1);
    const labelDriftDays = Math.abs(Math.round((end - impliedEnd.getTime()) / MS_PER_DAY));
    if (labelDriftDays > MAX_TERM_LABEL_DRIFT_DAYS) return null;

    const ratio = years / (termMonths / 12);
    if (ratio < MIN_PLAUSIBLE_RATIO || ratio > MAX_PLAUSIBLE_RATIO) return null;

    return years;
}

/**
 * How much of each contract year a term actually bills. Their sum is the whole-of-term
 * multiplier applied to the ANNUAL figures in sites[] / line_items.
 *
 * The term's own date range wins whenever it has one, because retailers do not quote whole
 * months. AGL's WPG SYDNEY offer is "16 Months 12 Days" — 20/08/2026 to 31/12/2027 is 498 days
 * = 1.364384 years, where termMonths / 12 − 1/365 gives 1.330594. That 2.47% shortfall printed
 * Commodity Spend at $18,834 against the client model's $19,311 and Other Charges at $6,358
 * against $6,519, on every AGL term (its 28/40/52-month offers carry the same 12-day tail).
 *
 * Shape of the result:
 *   • Year 1 keeps year1DayFraction — it still runs start date → the day before the
 *     anniversary, and that is a property of the contract year, not of the term.
 *   • Middle years bill in full.
 *   • The LAST slot absorbs whatever is left, which is where an odd-days tail lands.
 *
 * The slot count is capped at ceil(termMonths / 12) deliberately. Letting the remainder spill
 * into an extra slot would hand unpricedContractYears a year the retailer never quoted, and it
 * would drop the whole term from the comparison over a 12-day rounding tail.
 *
 * Falls back to the previous months-based form when the term carries no usable dates, so every
 * offer without a date range behaves exactly as before.
 */
/**
 * The client's own multiplier for a term, measured from the contract's first day.
 *
 * ⚠️ THE FALLBACK ADDED A WHOLE YEAR PER SLOT; THE CLIENT COUNTS RAW DAYS AND TAKES ONE OFF.
 *
 * Their Data Input Page prints the four numbers outright. ANVERS CONFECTIONERY starts
 * 01/10/2027 and its block reads 1 / 1.9972602739726 / 2.9972602739726 / 3.9972602739726 —
 * that is DATEDIF from the first day to each year's end, less one from Year 2 on:
 *
 *     Year 1   01/10/2027 -> 30/09/2028    365 days           365 / 365 = 1.00000
 *     Year 2   01/10/2027 -> 30/09/2029    730 days  less 1   729 / 365 = 1.99726
 *     Year 3   01/10/2027 -> 30/09/2030   1095 days  less 1  1094 / 365 = 2.99726
 *
 * The months-based fallback gave Year 1 + 1 per year — 1, 2, 3 — because its Year 1 already
 * counted the leap day. The two forms agree only when the leap day falls in contract year TWO,
 * which is why FIAZ (01/10/2026) and COWES (10/04/2026) matched to the cent and ANVERS did not:
 * every Shell and Aurora figure on it printed 0.14% high, energy, environmental and other alike,
 * while Avg. Yearly Spend — the one column no multiplier touches — matched exactly.
 *
 * This is the same arithmetic termWeightForWindow already does; it simply was not available to
 * an offer whose terms carry no dates, which is exactly ANVERS.
 */
export function clientTermWeightFromStart(
  contractStartMs: number,
  termMonths: number,
  /**
   * The tender's own Year-1 length in months. Without it a long Year 1 is mistaken for a later
   * year — see the note on the deduction below.
   */
  seriesYear1Months?: number | null,
): number | null {
  if (!Number.isFinite(contractStartMs) || !(termMonths > 0)) return null;
  const start = new Date(contractStartMs);
  // ⚠️ THE GRID IS ANCHORED TO THE FIRST OF THE START MONTH, NOT TO THE START DAY.
  //
  // COWES BAKEHOUSE begins 10/04/2026 and the client's Year 1 ends 31/03/2027 — the last day of
  // the month BEFORE the start month, a year on — which is 355 days, not the 364 an anniversary
  // gives. Their Data Input Page reads 0.972602739726027, and the anniversary form would print
  // every total on that tender 2.5% high. Identical for a start on the 1st, which is most of
  // prod, so the two only part company on a mid-month start. Same anchor as
  // computeYear1DayFraction.
  const end = new Date(Date.UTC(
    start.getUTCFullYear(), start.getUTCMonth() + termMonths, 1,
  ) - MS_PER_DAY);
  const rawDays = Math.round((end.getTime() - start.getTime()) / MS_PER_DAY);
  if (!Number.isFinite(rawDays) || rawDays <= 0) return null;
  // ⚠️ THE DEDUCTION BELONGS TO YEAR 2, AND YEAR 1 IS NOT ALWAYS TWELVE MONTHS.
  //
  // The client takes the minus one once, from the second contract year on. Keying that on
  // `termMonths > 12` reads any term longer than a year as a later year — but a tender that asks
  // for 14 / 26 / 38 / 50 has a FOURTEEN-month Year 1, and that first term was losing a day it
  // should keep.
  //
  // NPC GROUP's 14-month term: the client's sheet reads 425 days, this returned 424. Only
  // reachable when an offer carries no dates of its own, since the window path is preferred —
  // which is exactly why it went unnoticed.
  //
  // Where the tender states no series, twelve months is still the right assumption and the
  // behaviour is unchanged.
  const year1Months = Number.isFinite(seriesYear1Months) && (seriesYear1Months as number) > 0
    ? (seriesYear1Months as number)
    : 12;
  const isBeyondYear1 = termMonths > year1Months;
  return (rawDays - (isBeyondYear1 ? 1 : 0)) / 365;
}

/**
 * How many yearly slots a term occupies when the tender has STATED its series.
 *
 * ⚠️ ceil(months / 12) IS ONLY A GUESS, AND IT OVERCOUNTS A LONG YEAR 1.
 *
 * Battle Kart asked for 15 / 27 / 39 / 51 months and FoodWorks for 17 / 29 / 41 / 53. Their
 * 27- and 29-month terms are TWO periods — a long Year 1 plus one more year — but ceil gives
 * three, so the environmental average divided by a year the retailer never quoted, and
 * unpricedContractYears could drop the term outright for want of a year_3 that was never
 * supposed to exist. seriesPositionForWindow already does this for tenders whose offers carry
 * dates; this is the same rule for the ones that do not.
 *
 * Returns null when the tender states no series, or when the term does not sit on the series
 * grid — the ceil form then stands, exactly as before.
 */
function seriesSlotCount(termMonths: number, seriesYear1Months?: number | null): number | null {
  if (!Number.isFinite(seriesYear1Months) || !((seriesYear1Months as number) > 0)) return null;
  const monthsPastYear1 = termMonths - (seriesYear1Months as number);
  const position = Math.round(monthsPastYear1 / 12);
  if (position < 0) return null;
  if (Math.abs(monthsPastYear1 - position * 12) > WINDOW_TERM_LABEL_TOLERANCE_MONTHS) return null;
  return position + 1;
}

export function buildYearWeights(
  termMonths: number,
  year1DayFraction: number,
  termData: any,
  contractStartMs?: number | null,
  /** The tender's stated Year-1 length in months, when it has one — see seriesSlotCount. */
  seriesYear1Months?: number | null,
): { year: number; weight: number }[] {
  const maxSlots = seriesSlotCount(termMonths, seriesYear1Months)
    ?? Math.max(1, Math.ceil(termMonths / 12));
  const fromDates = termLengthInYearsFromDates(termData, termMonths);
  // Only where the term carries no dates of its own — the case that until now fell through to
  // the months-based approximation. A term WITH dates keeps the length those dates state.
  const fromStart = fromDates === null && contractStartMs != null
    ? clientTermWeightFromStart(contractStartMs, termMonths, seriesYear1Months)
    : null;
  const totalYears = fromDates ?? fromStart ?? (termMonths / 12 - (1 - year1DayFraction));
  if (!(totalYears > 0)) return [];

  // ⚠️ SPREAD EVENLY — DO NOT FILL SLOT BY SLOT.
  //
  // buildYearWeightsForWindow already spreads its total evenly, and these two must agree: a
  // tender's environmental total cannot depend on whether its offers happened to be parsed with
  // dates. The old loop filled each slot to capacity and dropped the remainder in the last one,
  // so a 24-month term weighed [1, 0.99726] when the start date gave 1.99726 years.
  //
  // Environmental is a WEIGHTED MEAN over the slots (averageEnvAcrossYears), and certificate
  // rates fall year on year, so over-weighting Year 1 pulled that mean up. ANVERS's 24-month
  // Shell figure came out $1,430.71/yr against the client workbook's $1,430.56 — $2,858 printed
  // where the client prints $2,857. FIAZ showed the same ~$2 gap on Shell's Years 2-4 with
  // Year 1 exact, which is the signature: Year 1 is a single slot and has nothing to skew.
  //
  // The slot COUNT is unchanged — ceil(totalYears) capped at maxSlots reproduces exactly what
  // the loop used to emit — and the weights still SUM to totalYears, which is the term_weight
  // that energy, others and every total are multiplied by. Only the split moves.
  // A stated series fixes the count outright; without one, ceil(totalYears) capped at maxSlots
  // reproduces exactly what the fill loop used to emit.
  const slots = seriesSlotCount(termMonths, seriesYear1Months)
    ?? Math.min(maxSlots, Math.max(1, Math.ceil(totalYears - 1e-9)));
  const share = totalYears / slots;
  return Array.from({ length: slots }, (_, i) => ({ year: i + 1, weight: share }));
}

// ============================================================================
// Tender-level comparison window
// ============================================================================

/**
 * The Year-1 window every offer in a tender is priced over, in days.
 *
 * ⚠️ THE WINDOW IS A PROPERTY OF THE TENDER, NOT OF EACH OFFER.
 *
 * Retailers answering one tender do not all quote the same end date. WPG SYDNEY's four
 * retailers were asked for a 16-month deal and returned:
 *
 *     AGL       20/08/2026 → 31/12/2027    498 days
 *     Flo       20/08/2026 → 31/12/2027    498 days
 *     Momentum  20/08/2026 → 19/12/2027    486 days
 *     Origin    20/08/2026 → 30/11/2027    467 days
 *
 * Pricing each over its own span makes the ranking meaningless — Origin looks $1,791 cheaper
 * than it is purely because it sells 31 fewer days. The client workbook states the window once,
 * at tender level ("Time Period if STEPPED: 1 Year 20/08/2026 – 31/12/2027"), and bills every
 * retailer over it. This function recovers that window from the offers themselves, so no new
 * field is needed.
 *
 * The FIRST PERIOD of each offer is its shortest term of at least a year. Sub-annual teasers are
 * skipped deliberately: AGL's pricing offer 1 is "4 Months 12 Days" ending 31/12/2026, and taking
 * the shortest term outright would collapse the window to 133 days.
 *
 * Returns null when no offer supplies a usable first period, leaving the caller on each term's
 * own dates.
 *
 * @param contractStart - Epoch ms UTC midnight; the day after the current contract expires
 * @param offerTerms - One contract_terms object per offer in the tender
 */
export type TenderYear1Window = {
    /** Leap-excluded days from the contract's first day to the end of the shared Year 1. */
    days: number;
    /** The month label the window was taken from — the requested term it normalises. */
    months: number;
    /** The contract's first day, in ms. Year N's weight is measured from it — see the client
     *  formula quoted on termWeightForWindow. */
    start: number;
    /** RAW days to the end of Year 1 — leap days INCLUDED. The client's DATEDIF counts them. */
    rawDays: number;
};

/**
 * How far a term's month label may sit from the window's before it is treated as a different
 * requested term rather than the same one quoted to a different end date.
 *
 * WPG's retailers answered a 16-month request with 16, 16, 16 and 15 months — all the same
 * request, and all belong in the same window. A 12-month offer in that tender would be a
 * genuinely shorter product and must keep its own length, or it would be billed for 134 days
 * it does not sell.
 */
const WINDOW_TERM_LABEL_TOLERANCE_MONTHS = 2;

/**
 * The contract's first day, taken from the offers themselves.
 *
 * ⚠️ THE WINDOW MUST NOT DEPEND ON tender_site.current_contract_expiry.
 *
 * That column is how the contract start is normally derived (the new contract starts the day
 * after the old one ends), but it is operator-entered and routinely left blank — the dev tender
 * that exposed this had it null on every site. With no start date the window cannot be built,
 * the engine silently falls back to each term's own dates, and retailers who quoted a shorter
 * contract are priced over fewer days again: Momentum at 486 days and Origin at 467 against
 * AGL's 498, which is the whole error the window exists to remove.
 *
 * Every parsed term already carries its own start date, and they agree — all four WPG retailers
 * quoted from 2026-08-20 — because the tender told them when supply begins. The earliest is the
 * contract's first day.
 *
 * Returns null only when no offer supplies a usable start date.
 */
export function tenderContractStartFromOffers(
    offerTerms: Array<Record<string, any> | null | undefined>,
): number | null {
    let earliest: number | null = null;
    for (const terms of offerTerms) {
        if (!terms || typeof terms !== 'object') continue;
        for (const value of Object.values(terms)) {
            if (!value || typeof value !== 'object') continue;
            const { start } = rangeOf(value);
            if (start === null) continue;
            if (earliest === null || start < earliest) earliest = start;
        }
    }
    return earliest;
}

export function tenderYear1Window(
    contractStart: number,
    offerTerms: Array<Record<string, any> | null | undefined>,
    /**
     * The tender's own Year-1 length in months, when it has stated one. Overrides the
     * twelve-month floor below — see the note there.
     */
    seriesYear1Months?: number | null,
): TenderYear1Window | null {
    // Each offer's own first period: its shortest term of a year or more.
    //
    // Sub-annual teasers are skipped deliberately. AGL's pricing offer 1 is "4 Months 12 Days"
    // ending 31/12/2026, and taking the shortest term outright would collapse the window to
    // 133 days and mis-price the entire tender.
    //
    // ⚠️ BUT TWELVE IS ONLY A GUESS AT WHERE YEAR 1 ENDS, AND A TENDER CAN SAY.
    //
    // ACR MACKAY asked for 10 / 22 / 34 / 46 months. Its Year 1 IS ten months, so the floor
    // threw away the only term that could form the window, no window was built, and every
    // offer fell back to buildYearWeights — which hands Year 1 the twelve-month day fraction
    // (0.99726) and dumps the remainder on the last year. For a 22-month term that is
    // 0.99726 / 0.83836: the ten-month stub weighted as a full year and the full year weighted
    // as a stub, exactly backwards.
    //
    // Commodity spend never showed it. A Smooth term is one flat rate times the whole-of-term
    // multiplier, so how the years split does not matter — every retailer's energy matched the
    // client to the dollar. Environmental rates DO change year to year, so the reversed split
    // landed there alone: Origin printed $2,325 against the client's $2,297, Momentum $2,369
    // against $2,338, and so on for every retailer except AGL, whose environmental is quoted
    // cumulatively and reads the same whichever way the years are weighted.
    //
    // When the tender has stated its series there is nothing to guess: Year 1 is what it says.
    // Without one the twelve-month floor stands, so every tender that has not set a series
    // behaves exactly as it does today.
    const statedYear1 = Number.isFinite(seriesYear1Months) && (seriesYear1Months as number) > 0
        ? (seriesYear1Months as number)
        : null;
    const minMonths = statedYear1 ?? 12;
    const firsts: Array<{ months: number; days: number; rawDays: number; start: number }> = [];

    for (const terms of offerTerms) {
        if (!terms || typeof terms !== 'object') continue;
        let bestMonths = Infinity;
        let bestDays: number | null = null;
        let bestRawDays: number | null = null;
        let bestStart: number | null = null;

        for (const [key, value] of Object.entries(terms)) {
            if (!value || typeof value !== 'object') continue;
            const months = parseInt(String(key).replace(/[^0-9].*$/, ''), 10);
            if (!Number.isFinite(months) || months < minMonths || months >= bestMonths) continue;

            // ⚠️ MEASURE THE SPAN WITHIN ONE SOURCE — THE TERM'S OWN RANGE.
            //
            // `contractStart` is derived from the site's current-contract expiry plus a day. The
            // END comes from the retailer's quote. Pairing the two measures from a date the
            // retailer never quoted, and the result is one day out whenever they disagree.
            //
            // NPC GROUP: the site's contract expires 30/10/2026, so contractStart was 31/10;
            // every retailer quoted from 01/11/2026 to 31/12/2027. We measured 426 days where
            // the client's sheet reads 425, and that single day rode through all four terms —
            // 426/791/1156/1521 against their 425/790/1155/1520. Avg. Yearly Spend matched to
            // the dollar on every row, because it divides the multiplier back out; every
            // commodity, environmental, other and total figure came out 0.07-0.24% high.
            const { start: quotedStart, end } = rangeOf(value);
            const spanStart = quotedStart ?? contractStart;
            if (end === null || end <= spanStart) continue;

            const spanRawDays = Math.round((end - spanStart) / MS_PER_DAY);
            const spanDays = spanRawDays - countLeapDays(spanStart, end);
            // Same plausibility band the per-term form uses — one offer whose parsed range spans
            // decades must not be allowed to define the window for the whole tender.
            const ratio = (spanDays / 365) / (months / 12);
            if (ratio < MIN_PLAUSIBLE_RATIO || ratio > MAX_PLAUSIBLE_RATIO) continue;

            bestMonths = months;
            bestDays = spanDays;
            bestRawDays = spanRawDays;
            bestStart = spanStart;
        }

        if (bestDays !== null) firsts.push({ months: bestMonths, days: bestDays, rawDays: bestRawDays!, start: bestStart! });
    }

    if (firsts.length === 0) return null;

    // ⚠️ A STATED YEAR 1 IS NOT A GUESS, AND A LONGER ANSWER MUST NOT REDEFINE IT.
    //
    // The bucketing below treats month labels within two of each other as one request and keeps
    // the LONGEST quote in it. That is right while the window is being INFERRED — WPG's four
    // retailers answered one 16-month request with 16, 16, 16 and 15, and Year 1 is the longest
    // of them, because nothing else says where Year 1 ends.
    //
    // It is wrong the moment the tender has said. EXCH asked for 10 / 22 / 34 / 46 months from
    // 01/03/2027. AGL, Flo, Momentum and Origin quoted the ten (to 31/12/2027 — 305 days);
    // Energy Australia and Shell answered with a twelve (to 29/02/2028 — 365 raw days). Twelve
    // is within two of ten, so it joined the ten-month bucket, and being longer it TOOK it. The
    // tender's Year 1 became 365 days instead of 305.
    //
    // Every multiplier then landed on a whole number — 1 / 2 / 3 / 4 where the client's sheet
    // reads 305/365, 670/365, 1035/365 and 1400/365, i.e. 0.835616 / 1.835616 / 2.835616 /
    // 3.835616. Nothing else was wrong: every per-year figure matched the client's report to the
    // dollar (AGL's 22-month average yearly spend printed 23,119 against their 23,119), and
    // every whole-of-term total came out 19.7% high, on every retailer and every term, because
    // one number was 1 instead of 0.8356.
    //
    // When the tender states its Year 1, only the offers that actually quoted it form the
    // window. If none did, the inference below stands unchanged.
    const quotedStatedYear1 = statedYear1 === null
        ? []
        : firsts.filter(f => f.months === statedYear1);
    const windowCandidates = quotedStatedYear1.length > 0 ? quotedStatedYear1 : firsts;

    // ⚠️ THE MODAL REQUEST, NOT THE LONGEST.
    //
    // Retailers answering one tender do not all quote the same minimum term. Taking the longest
    // first period lets a single retailer who only offered 36 months declare a 1094-day "Year 1"
    // for the whole tender — measured across prod that moved 351 whole-year terms by up to
    // +46.4% across 61 tenders, which is far worse than the odd-term error being fixed. The
    // request most retailers answered is the one the tender actually asked for; ties go to the
    // shorter, which can only ever under-state.
    const buckets = new Map<number, { months: number; days: number; rawDays: number; start: number; count: number }>();
    for (const first of windowCandidates) {
        const key = [...buckets.keys()].find(m => Math.abs(m - first.months) <= WINDOW_TERM_LABEL_TOLERANCE_MONTHS);
        if (key === undefined) {
            buckets.set(first.months, { months: first.months, days: first.days, rawDays: first.rawDays, start: first.start, count: 1 });
            continue;
        }
        const bucket = buckets.get(key)!;
        bucket.count += 1;
        // Within one request, the longest quote is the window — it has to cover every retailer's
        // first period, and it is what the client workbook records.
        if (first.days > bucket.days) {
            bucket.days = first.days;
            bucket.rawDays = first.rawDays;
            bucket.months = first.months;
            // The start travels with the span it was measured from; keeping the old one would
            // reintroduce the very mismatch this measures around.
            bucket.start = first.start;
        }
    }

    let chosen: { months: number; days: number; rawDays: number; start: number; count: number } | null = null;
    for (const bucket of buckets.values()) {
        if (!chosen || bucket.count > chosen.count
            || (bucket.count === chosen.count && bucket.months < chosen.months)) {
            chosen = bucket;
        }
    }
    return chosen ? { days: chosen.days, months: chosen.months, start: chosen.start, rawDays: chosen.rawDays } : null;
}

/**
 * Whole-of-term multiplier for a term priced over the tender's shared window.
 *
 * A tender asks for a series of terms — WPG SYDNEY asked for 16 / 28 / 40 / 52 months, all
 * starting 20/08/2026 and each ending a calendar year later than the last. The window fixes
 * Year 1 at 498 days; every term after it adds exactly one year. That is the client workbook's
 * own table, and it is a property of the SERIES, not of any term's parsed day count:
 *
 *     16 months → 1.36438356164384        40 months → 3.36438356164384
 *     28 months → 2.36438356164384        52 months → 4.36438356164384
 *
 * Returns null when the term is not a member of the window's series, leaving the caller on the
 * months-based form. That guard is load-bearing: a genuine 24-month term inside a 16-month
 * series is 8 months from Year 1 and 4 months from Year 2, so it belongs to neither. Stretching
 * it to Year 2 anyway moved 207 whole-year terms across 36 prod tenders, by up to 37.7%.
 *
 * Standard tenders are members of their own series by construction — a 12-month window makes
 * 12 / 24 / 36 / 48 months positions 0 / 1 / 2 / 3, returning 364/365 + k, which is
 * termWeightFor() exactly.
 */
export function termWeightForWindow(
    termMonths: number,
    window: TenderYear1Window,
): number | null {
    if (!(termMonths > 0) || !(window.days > 0)) return null;

    // ⚠️ A WHOLE-YEAR TERM NEVER MOVES UNLESS THE WINDOW IS ITSELF AN ANNIVERSARY YEAR.
    //
    // 12 / 24 / 36 / 48 months are round-years quotes and the anniversary convention is already
    // right for them. When a tender's own series is odd — say 13 / 25 / 37 — a 24-month offer
    // does answer the 25-month request, and pricing it over that window is arguably more correct
    // than leaving it. But it is still a standard term changing value, and 144 of them across 15
    // prod tenders moved by up to 16.8% before this guard. A standard term keeping the figure it
    // prints today is worth more than that correction.
    //
    // Standard tenders are unaffected: their window IS an anniversary year, so every whole-year
    // term still goes through the series below and returns 364/365 + k — termWeightFor() exactly.
    // Exactly 364, never 365. A correctly dated whole year is always 364 leap-excluded days —
    // start date to the day before the anniversary — for every start date, leap or not. A 365-day
    // first period is an end date parsed one day long, and 21 prod whole-year terms were riding
    // on it.
    const windowIsAnniversary = window.days === 364;
    if (termMonths % 12 === 0 && !windowIsAnniversary) return null;

    // The term's position in the series: how many whole years past Year 1 it sits.
    const monthsPastYear1 = termMonths - window.months;
    const position = Math.round(monthsPastYear1 / 12);
    if (position < 0) return null;
    if (Math.abs(monthsPastYear1 - position * 12) > WINDOW_TERM_LABEL_TOLERANCE_MONTHS) return null;

    // ⚠️ THE CLIENT'S OWN FORMULA, CELL FOR CELL.
    //
    // Their Data Input Page computes each year's multiplier straight from the contract start:
    //
    //     Year 1     =  DATEDIF(start, year_1_end, "D") / 365
    //     Year N ≥ 2 = (DATEDIF(start, year_N_end, "D") − 1) / 365
    //
    // Raw days — leap days are COUNTED, and the −1 is taken once, from the second row on. This
    // reproduces all twelve weights across the three reports they have sent:
    //
    //     WERRIBEE  start 01/09/26    121  /  486−1=485  /  852−1=851  /  1217−1=1216
    //     PRINCE    start 01/10/26    456  /  822−1=821  / 1187−1=1186 / 1552−1=1551
    //     COWES     start 10/04/26    355  /  721−1=720  / 1086−1=1085 / 1451−1=1450
    //
    // ⚠️ WHY THE OLD FORM LOOKED RIGHT FOR SO LONG.
    //
    // It returned window.days/365 + position, on leap-EXCLUDED days: Year 1's span less its leap
    // days, plus exactly 365 for every year after. Against the client's form the two differ by
    // (leap days after Year 1) − 1, so they agree exactly whenever the years past Year 1 contain
    // ONE leap day — which is every term in PRINCE and COWES, and Years 3 and 4 of WERRIBEE.
    // Eleven of the twelve weights matched by coincidence. The twelfth, WERRIBEE's 16-month
    // term, spans no leap day at all: we printed 486 where the client's sheet reads 485, and
    // every Year 2 figure in that tender came out 0.2% high.
    const year1EndMs = window.start + window.rawDays * MS_PER_DAY;
    const year1End = new Date(year1EndMs);

    // ⚠️ A YEAR 1 ENDING 29 FEBRUARY MUST NOT ROLL INTO 1 MARCH.
    //
    // setUTCFullYear(+n) on 29/02 lands on a date that does not exist in a common year, and
    // JavaScript normalises it forward to 1 March — one day too many, every year after Year 1.
    //
    // LULLSCOPE INVESTMENTS runs 01/03/2027 – 29/02/2028. Year 2's end became 01/03/2029 instead
    // of 28/02/2029, so the span read 731 days and the multiplier came out (731−1)/365 = 2.000000
    // where the client's sheet reads (730−1)/365 = 1.997260. Energy Australia's 24-month total
    // printed $38,989 against their $38,935, the 36-month $59,405 against $59,351 and the
    // 48-month $80,010 against $79,955 — 0.14% high on every retailer, every column, while
    // Avg. Yearly Spend ($19,494), which no multiplier touches, matched exactly.
    //
    // Clamping to the last day of the target month is what the client's own grid does: their
    // Year N ends on the day before the contract's anniversary, which for a 1 March start is
    // 28 February in a common year. It changes nothing for any other end date — only 29 February
    // has no counterpart in the year it is carried into.
    const targetYear = year1End.getUTCFullYear() + position;
    const targetMonth = year1End.getUTCMonth();
    const lastDayOfTargetMonth = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();

    // ⚠️ A MONTH-END STAYS A MONTH-END.
    //
    // Contract years end on a month boundary, so carrying one forward has to land on the target
    // month's own last day, not on the same day number. 28/02/2027 is the end of February; a year
    // on, the end of February is the 29th. Clamping alone kept the 28th and lost the leap day, so
    // a 26-month term measured 787 days where counting 26 months from the contract start gives
    // 788 — the one case where this form and clientTermWeightFromStart still disagreed.
    //
    // It cuts both ways and both are right: 29/02/2028 carried forward is 28/02/2029.
    const year1EndsMonth = year1End.getUTCDate()
        === new Date(Date.UTC(year1End.getUTCFullYear(), year1End.getUTCMonth() + 1, 0)).getUTCDate();
    const targetDay = year1EndsMonth
        ? lastDayOfTargetMonth
        : Math.min(year1End.getUTCDate(), lastDayOfTargetMonth);
    const yearNEndMs = Date.UTC(targetYear, targetMonth, targetDay);

    const rawDays = Math.round((yearNEndMs - window.start) / MS_PER_DAY);
    if (!Number.isFinite(rawDays) || rawDays <= 0) return null;

    return (rawDays - (position >= 1 ? 1 : 0)) / 365;
}

/**
 * A term's place in the tender's series: Year 1 is 0, Year 2 is 1, and so on.
 *
 * The same arithmetic termWeightForWindow does, named once so the slot count and the multiplier
 * cannot drift apart. Returns null for a term the series does not describe, exactly as the
 * weight does — callers then fall back to the months-based form.
 */
const seriesPositionForWindow = (
    termMonths: number,
    window: TenderYear1Window,
): number | null => {
    const monthsPastYear1 = termMonths - window.months;
    const position = Math.round(monthsPastYear1 / 12);
    if (position < 0) return null;
    if (Math.abs(monthsPastYear1 - position * 12) > WINDOW_TERM_LABEL_TOLERANCE_MONTHS) return null;
    return position;
};

/**
 * How many contract years a term spans within the tender's series — the number of year slots
 * its costs are split across, each carrying an EQUAL share of the multiplier.
 */
export function contractYearCountForWindow(
    termMonths: number,
    window: TenderYear1Window,
): number {
    const weight = termWeightForWindow(termMonths, window);
    if (weight === null) return 1;
    // Same rule as buildYearWeightsForWindow — these two must never disagree: one says how many
    // years a term has, the other splits the term across them.
    return (seriesPositionForWindow(termMonths, window) ?? 0) + 1;
}

/**
 * Per-contract-year weights for a term priced over the tender's shared window: N equal shares
 * of the whole-of-term multiplier.
 *
 * Equal shares are the point. The caller sums `cost(year) x weight` and divides by the weights
 * it used, so equal shares make that a plain mean over the years the offer prices — which is what
 * the client model does. The old unequal shape (0.99726 for Year 1, a full 1 each after,
 * remainder onto the tail) agrees to within a dollar on any whole-year term, but weights the
 * escalated tail year far too heavily on an unconventional one.
 *
 * ⚠️ THE COUNT IS ceil(), NOT round(). A TERM SPANS EVERY YEAR IT TOUCHES.
 *
 * This used to round, which gave a 16-month term ONE year (round(1.3315) = 1) and a 28-month term
 * two. The term was then priced entirely on its first year's environmental rates, and on its
 * first year's energy rates if stepped — the four extra months, which fall in the NEXT calendar
 * year at that year's higher certificate prices, cost nothing at all.
 *
 * WERRIBEE RACING CLUB's own tender report settles it. Every figure below reproduces to the
 * dollar with ceil, and none of them with round:
 *
 *   Momentum 16mo env      3,207 = mean(2,281 , 2,547)         x 485/365    <- 2 slots
 *   Momentum 28mo env      5,602 = mean(2,281 , 2,547 , 2,380) x 851/365    <- 3 slots
 *   ALINTA   16mo energy  14,705 = mean(10,635 , 11,499)       x 485/365    <- 2 years, stepped
 *   Engie    16mo energy  18,953 = mean(15,244 , 13,283)       x 485/365
 *   NextBus  16mo env      1,667 = mean(0 , 2,509)             x 485/365
 *
 * That last line is the other half of it: Next Business and Flo quote no 2026 environmental row
 * at all, and the client's model counts the missing year as ZERO and still divides by two. With
 * round() the term saw only that empty first slot and reported no environmental charge whatsoever
 * — Next Business's 16-month environmental printed as $0 against their $1,667.
 *
 * Whole-year terms are untouched: their weight sits just under a whole number (12 months =
 * 364/365 = 0.99726), where ceil and round both give 1, and the same holds at 24, 36 and 48.
 *
 * Returns an empty array when the term is not in the window's series, so the caller falls back.
 */
/**
 * ⚠️ THE SLOT COUNT IS THE TERM'S PLACE IN THE SERIES, NOT ITS MULTIPLIER.
 *
 * This used to be `ceil(weight − 0.1)`: how many whole years the multiplier spans. That is the
 * same number as the position for every tender whose Year 1 is a year or less, which is every
 * shape seen until now — 12 months on a standard tender, 4 on WERRIBEE, 10 on ACR MACKAY. The
 * two rules only part company when YEAR 1 ITSELF IS LONGER THAN A YEAR.
 *
 * ARMIDALE asked for 16 / 28 / 40 / 52 months from 01/09/2026, so its Year 1 runs 486 days and
 * weighs 1.3315. Every term then drew one slot too many:
 *
 *     16 months   Year 1   1 slot    ceil(1.2315) = 2
 *     28 months   Year 2   2 slots   ceil(2.2315) = 3
 *     40 months   Year 3   3 slots   ceil(3.2315) = 4
 *     52 months   Year 4   4 slots   ceil(4.2315) = 5
 *
 * Commodity never showed it — a Smooth term is one flat rate, so splitting it four ways or five
 * gives the same money, and every energy figure matched the client to the dollar. Environmental
 * rates change year to year, so the extra slot landed there alone and nowhere else: Origin's
 * 16-month environmental printed $2,338 against the client's $2,467, and all twelve tiled
 * figures across Origin, Nectr and Flo were out. AGL's matched throughout, because its
 * environmental is quoted cumulatively and reads the Nth slot rather than a mean of N.
 *
 * Position is what the client's own model uses — their Year N row averages the first N
 * certificate years — and it is already computed to place the term. Reading it directly makes
 * the count exact instead of inferred, and every earlier shape keeps the number it had.
 */
export function buildYearWeightsForWindow(
    termMonths: number,
    window: TenderYear1Window,
): { year: number; weight: number }[] {
    const total = termWeightForWindow(termMonths, window);
    if (total === null || !(total > 0)) return [];
    const position = seriesPositionForWindow(termMonths, window);
    if (position === null) return [];
    const years = position + 1;
    const share = total / years;
    return Array.from({ length: years }, (_, i) => ({ year: i + 1, weight: share }));
}

/**
 * A term's own length in days, leap days excluded, read from its quoted date range.
 *
 * Same guards as termLengthInYearsFromDates, minus the whole-year exclusion — under the shared
 * window a correctly dated standard term reproduces termWeightFor() on its own, so there is
 * nothing to protect it from. The label-corroboration guard still rejects the 362 prod terms
 * whose parsed end date contradicts their month count by up to 92 days.
 *
 * Returns null when the dates are absent or untrustworthy, so the caller falls back.
 */
export function termSpanDaysFromDates(termData: any, termMonths: number): number | null {
    if (!(termMonths > 0)) return null;

    let { start, end } = rangeOf(termData);
    if (start === null || end === null) {
        for (const key of Object.keys(termData || {})) {
            if (!/^year_\d+$/.test(key)) continue;
            const yearRange = rangeOf((termData as any)[key]);
            if (yearRange.start !== null) start = start === null ? yearRange.start : Math.min(start, yearRange.start);
            if (yearRange.end !== null) end = end === null ? yearRange.end : Math.max(end, yearRange.end);
        }
    }
    if (start === null || end === null || end <= start) return null;

    const days = Math.round((end - start) / MS_PER_DAY) - countLeapDays(start, end);
    if (!Number.isFinite(days) || days <= 0) return null;

    const impliedEnd = new Date(start);
    impliedEnd.setUTCMonth(impliedEnd.getUTCMonth() + termMonths);
    impliedEnd.setUTCDate(impliedEnd.getUTCDate() - 1);
    if (Math.abs(Math.round((end - impliedEnd.getTime()) / MS_PER_DAY)) > MAX_TERM_LABEL_DRIFT_DAYS) return null;

    const ratio = (days / 365) / (termMonths / 12);
    if (ratio < MIN_PLAUSIBLE_RATIO || ratio > MAX_PLAUSIBLE_RATIO) return null;

    return days;
}
